// partners/adminView/pages/A0_AdminLayout.jsx
// Shared sidebar layout used by every admin page.
// Handles collapse, active-route highlight, and nav links.

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { endSession } from "../../../utils/logout";
import { capturePartnerEvent } from "../../../utils/analytics";

const NAV = [
  { path: "/partners/admin",              icon: "📊", label: "Overview" },
  { path: "/partners/admin/applications", icon: "📥", label: "Applications" },
  { path: "/partners/admin/partners",     icon: "🤝", label: "Partners" },
  { path: "/partners/admin/commissions",  icon: "💼", label: "Commissions" },
  { path: "/partners/admin/payouts",      icon: "💰", label: "Payouts" },
  { path: "/partners/admin/milestones",   icon: "🏆", label: "Milestones" },
  { path: "/partners/admin/interest",     icon: "⭐", label: "Coming Soon" },
];

export default function AdminLayout({ children }) {
  const location   = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="bg-mnf-dark-1 min-h-screen flex font-inter text-mnf-text-primary">

      {/* ── Sidebar ─────────────────────────────────────── */}
      <aside
        className={`${
          collapsed ? "w-16" : "w-[220px]"
        } shrink-0 bg-mnf-dark-2 border-r border-white/[0.08] min-h-screen p-4 flex flex-col transition-all duration-200`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          {!collapsed && (
            <div>
              <img
                src="https://cdn.prod.website-files.com/6890527cc3ef5b2eae4f1b43/68f32a89189c53cd269b54de_MNF_beta_light.svg"
                alt="MNF"
                className="h-6 mb-1"
              />
              <span className="text-[10px] font-poppins font-semibold text-mnf-cyan uppercase tracking-widest">
                Partner Admin
              </span>
            </div>
          )}
          <button
            onClick={() => {
              const next = !collapsed;
              setCollapsed(next);
              capturePartnerEvent("partner_admin_sidebar_toggled", { collapsed: next });
            }}
            className="text-mnf-text-muted hover:text-mnf-text-primary text-lg transition-colors leading-none"
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        {/* Nav */}
        <nav className="space-y-1 flex-1">
          {NAV.map(({ path, icon, label }) => {
            const active =
              location.pathname === path ||
              (path !== "/partners/admin" &&
                location.pathname.startsWith(path));
            return (
              <Link
                key={path}
                to={path}
                onClick={() => capturePartnerEvent("partner_admin_nav_clicked", { destination: path })}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg font-poppins text-[13px] font-medium transition-all ${
                  active
                    ? "bg-mnf-gradient text-mnf-btn-text"
                    : "text-mnf-text-secondary hover:bg-mnf-dark-4 hover:text-mnf-text-primary"
                }`}
              >
                <span className="text-base shrink-0">{icon}</span>
                {!collapsed && label}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="pt-6 border-t border-white/[0.06] space-y-3">
          <button
            type="button"
            onClick={() => endSession({ page: "partner_admin", source: "sidebar" })}
            title="Log out"
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg font-poppins text-[13px] font-medium text-mnf-text-secondary hover:bg-mnf-dark-4 hover:text-mnf-text-primary transition-all ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <span className="text-base shrink-0">↩</span>
            {!collapsed && "Log out"}
          </button>
          {!collapsed && (
            <Link
              to="/become-a-partner"
              className="block text-xs text-mnf-text-muted hover:text-mnf-cyan transition-colors"
            >
              ← Partner Program
            </Link>
          )}
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────── */}
      <main className="flex-1 p-8 min-h-screen overflow-auto">
        {children}
      </main>
    </div>
  );
}
